import { useEffect, useState, useCallback } from "react";
import PageShell from "@/components/PageShell";
import api, { apiError } from "@/lib/api";
import { formatRupiah, formatNumber, formatDate, todayWITA } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import DatePicker from "@/components/DatePicker";
import { Plus } from "lucide-react";
import { SupplierName } from "@/components/SupplierName";

export default function Returns() {
  const [returns, setReturns] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    const p = filter !== "all" ? `?supplier_id=${filter}` : "";
    api.get(`/returns${p}`).then((r) => setReturns(r.data));
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)); }, []);
  const supName = (id) => suppliers.find((s) => s.id === id)?.name || "-";

  return (
    <PageShell title="Retur & Kredit Supplier">
      <div className="flex items-center justify-between gap-3">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-52" data-testid="return-supplier-filter"><SelectValue placeholder="Supplier" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Supplier</SelectItem>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <ReturnDialog open={open} setOpen={setOpen} suppliers={suppliers} onSaved={load} />
      </div>
      <Card className="shadow-sm"><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Kode</TableHead><TableHead>Supplier</TableHead><TableHead>Tanggal</TableHead><TableHead>Jenis Kain</TableHead>
            <TableHead className="text-right">Yard</TableHead><TableHead className="text-right">Nominal</TableHead>
            <TableHead className="text-right">Tersedia</TableHead><TableHead className="text-right">Teralokasi</TableHead><TableHead className="text-right">Terpakai</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {returns.map((r) => (
              <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`return-row-${r.code}`}>
                <TableCell className="font-mono text-xs">{r.code}</TableCell>
                <TableCell><SupplierName name={supName(r.supplier_id)} id={r.supplier_id} /></TableCell>
                <TableCell>{formatDate(r.return_date)}</TableCell>
                <TableCell>{r.fabric_name}</TableCell>
                <TableCell className="text-right font-mono">{formatNumber(r.total_yards)}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{formatRupiah(r.amount)}</TableCell>
                <TableCell className="text-right font-mono text-emerald-600">{formatRupiah(r.available)}</TableCell>
                <TableCell className="text-right font-mono text-sky-600">{formatRupiah(r.allocated)}</TableCell>
                <TableCell className="text-right font-mono text-slate-500">{formatRupiah(r.used)}</TableCell>
              </TableRow>
            ))}
            {returns.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">Belum ada retur.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
    </PageShell>
  );
}

function ReturnDialog({ open, setOpen, suppliers, onSaved }) {
  const [form, setForm] = useState({ supplier_id: "", return_date: todayWITA(), fabric_type_id: "", total_yards: "", price_per_yard: "", reason: "" });
  const [fabrics, setFabrics] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [srcInv, setSrcInv] = useState([]);

  useEffect(() => { api.get("/fabric-types").then((r) => setFabrics(r.data)); }, []);
  useEffect(() => {
    if (form.supplier_id) api.get(`/invoices?supplier_id=${form.supplier_id}`).then((r) => setInvoices(r.data));
  }, [form.supplier_id]);

  const amount = (Number(form.total_yards) || 0) * (Number(form.price_per_yard) || 0);

  const submit = async () => {
    if (!form.supplier_id || !form.fabric_type_id) return toast.error("Lengkapi supplier & jenis kain");
    if (!(Number(form.total_yards) > 0)) return toast.error("Isi total yard");
    try {
      await api.post("/returns", {
        supplier_id: form.supplier_id, return_date: form.return_date, source_invoice_ids: srcInv,
        fabric_type_id: form.fabric_type_id, total_yards: Number(form.total_yards),
        price_per_yard: Number(form.price_per_yard) || 0, reason: form.reason,
      });
      toast.success("Retur disimpan sebagai kredit supplier");
      setOpen(false);
      setForm({ supplier_id: "", return_date: todayWITA(), fabric_type_id: "", total_yards: "", price_per_yard: "", reason: "" });
      setSrcInv([]);
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-return-button"><Plus className="h-4 w-4 mr-1.5" />Catat Retur</Button></DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Catat Retur Kain</DialogTitle><DialogDescription className="sr-only">Form pencatatan retur kain sebagai kredit supplier</DialogDescription></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5"><Label>Supplier</Label>
            <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
              <SelectTrigger data-testid="return-supplier-select"><SelectValue placeholder="Pilih" /></SelectTrigger>
              <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Tanggal Retur</Label><DatePicker value={form.return_date} onChange={(v) => setForm((f) => ({ ...f, return_date: v }))} testid="return-date-input" /></div>
          <div className="space-y-1.5"><Label>Jenis Kain</Label>
            <Select value={form.fabric_type_id} onValueChange={(v) => setForm({ ...form, fabric_type_id: v })}>
              <SelectTrigger data-testid="return-fabric-select"><SelectValue placeholder="Pilih" /></SelectTrigger>
              <SelectContent>{fabrics.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Total Yard Retur</Label><Input type="number" value={form.total_yards} onChange={(e) => setForm({ ...form, total_yards: e.target.value })} data-testid="return-yards-input" /></div>
          <div className="space-y-1.5"><Label>Harga Retur / Yard</Label><Input type="number" value={form.price_per_yard} onChange={(e) => setForm({ ...form, price_per_yard: e.target.value })} data-testid="return-price-input" /></div>
          <div className="space-y-1.5"><Label>Nominal Retur</Label><div className="h-10 flex items-center font-mono font-bold text-slate-900" data-testid="return-amount">{formatRupiah(amount)}</div></div>
        </div>
        <div className="space-y-1.5">
          <Label>Referensi Nota Asal (opsional)</Label>
          <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto border rounded p-2">
            {invoices.map((i) => (
              <button key={i.id} type="button" onClick={() => setSrcInv(srcInv.includes(i.id) ? srcInv.filter((x) => x !== i.id) : [...srcInv, i.id])}
                className={`text-xs px-2 py-1 rounded border ${srcInv.includes(i.id) ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`} data-testid={`return-src-${i.code}`}>
                {i.invoice_number}
              </button>
            ))}
            {invoices.length === 0 && <span className="text-xs text-slate-400">Pilih supplier dulu</span>}
          </div>
        </div>
        <div className="space-y-1.5"><Label>Alasan / Keterangan</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="return-reason-input" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="return-cancel-button">Batal</Button>
          <Button onClick={submit} data-testid="return-submit-button">Simpan Retur</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
